import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AccessDeniedCard from './AccessDeniedCard';
import { config } from '../../config/env';
import { getHubDashboardHref } from '../../utils/urlNormalize';

/**
 * MUI rebuild of the reference PolicyDeniedPane — the "this area is blocked by
 * policy" card plus its centering wrapper. Same props/behavior as the reference.
 *
 * Generic on purpose: every string, size and behaviour is a prop, so the same component
 * serves a full-tab denial, a narrow sidebar denial, or any other gated region. Nothing
 * here is feature-specific — pass the action's own copy in.
 *
 * @example Full-width tab denial with guidance copy
 *   <PolicyDeniedPane reason={deniedReason} size="wide" showGuidance />
 *
 * @example Narrow sidebar denial
 *   <PolicyDeniedPane title="Hierarchy access denied" reason={reason} size="sidebar" />
 */

/** Numbered guidance step — small circular brand badge + explanatory line. */
function GuidanceStep({ index, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          height: 20,
          width: 20,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: 'rgba(22,138,173,0.12)',
          color: '#127190',
          fontSize: 10,
          fontWeight: 700,
        }}
      >
        {index}
      </Box>
      <Typography variant="body2" component="p" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
}

GuidanceStep.propTypes = {
  index: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

export default function PolicyDeniedPane({
  /** Card heading. Omit to use AccessDeniedCard's own default. */
  title,
  /** Policy reason from the denial parser (falls back to `fallbackReason`). */
  reason,
  fallbackReason = 'Access was denied by current policies.',
  /**
   * Layout preset:
   * - 'wide'    full-height tab region, card capped at max width
   * - 'sidebar' fixed 360px column, card fills it
   * - 'fill'    fills its parent, card capped at max width
   */
  size = 'wide',
  /** Show the "refresh reloads your session / request access" explanatory paragraphs. */
  showGuidance = false,
  /** Hub (or other IdP) link for the Request-for-Access button. */
  requestAccessHref = getHubDashboardHref(config.HUB_LOGIN_URL),
  /** Defaults to a full page reload, which re-pulls policies. */
  onRefresh = () => window.location.reload(),
  showRefresh = true,
  /** Extra classes for the outer wrapper. */
  className,
  /** Extra body content rendered under the divider (overrides `showGuidance`). */
  children,
}) {
  // Faint brand-tinted radial wash instead of a flat gray fill — the card should
  // look like it belongs to this product, not like a browser error page.
  const backdropSx = (theme) => ({
    bgcolor: theme.palette.mode === 'dark' ? '#0B0F14' : '#F8FAFC',
    backgroundImage:
      theme.palette.mode === 'dark'
        ? 'radial-gradient(ellipse 800px 500px at 50% -10%, rgba(22,138,173,0.10), transparent)'
        : 'radial-gradient(ellipse 800px 500px at 50% -10%, rgba(22,138,173,0.06), transparent)',
  });

  const wrapperSx = {
    wide: {
      display: 'flex',
      minHeight: 'calc(100dvh - 8rem)',
      width: '100%',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    },
    sidebar: {
      display: 'flex',
      height: '100%',
      width: 360,
      maxWidth: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    },
    fill: {
      display: 'flex',
      height: '100%',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    },
  }[size] || {
    display: 'flex',
    height: '100%',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    p: 3,
  };

  const cardSx = size === 'sidebar' ? { width: '100%' } : { width: '100%', maxWidth: 448 };

  const body =
    children ??
    (showGuidance ? (
      <>
        <GuidanceStep index={1}>
          Refresh — this reloads your session and pulls the latest access policies. If your
          permissions were just updated, this is usually all it takes.
        </GuidanceStep>
        <GuidanceStep index={2}>
          Still blocked? Use{' '}
          <Typography component="strong" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Request for Access
          </Typography>{' '}
          to open Hub, or contact your study administrator.
        </GuidanceStep>
      </>
    ) : null);

  return (
    <Box className={className} sx={[backdropSx, wrapperSx]}>
      <AccessDeniedCard
        role="alert"
        sx={{ ...cardSx, ...(size === 'wide' ? { flexShrink: 0 } : {}) }}
        {...(title ? { title } : {})}
        subtitle={reason || fallbackReason}
        showRefresh={showRefresh}
        onRefresh={onRefresh}
        requestAccessHref={requestAccessHref}
      >
        {body}
      </AccessDeniedCard>
    </Box>
  );
}

PolicyDeniedPane.propTypes = {
  title: PropTypes.string,
  reason: PropTypes.string,
  fallbackReason: PropTypes.string,
  size: PropTypes.oneOf(['wide', 'sidebar', 'fill']),
  showGuidance: PropTypes.bool,
  requestAccessHref: PropTypes.string,
  onRefresh: PropTypes.func,
  showRefresh: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};
